import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button, notification } from 'antd';
import { useHistory } from 'react-router-dom';
import EditableTable from './EditableTable';
import { renderFieldValue, useGetMaxWidth } from 'helper/common';
import { useRenderReusableControls } from 'helper/reusableControls';
import { isMobile } from 'utils/detectDevice';
import {
  P0_ISSUE_TYPES,
  P0_ISSUE_TYPE_HEADERS,
  P1_ISSUE_TYPES,
  P1_ISSUE_TYPE_HEADERS,
} from 'farmx-web-ui/src/components/constants';
import { FaChevronLeft } from 'react-icons/fa';

const defaultIssueType = 'default';

const toTitleCase = (str: string) =>
  str
    ?.split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

interface Issue {
  type?: string;
  [key: string]: any;
}

const priorityOptions = [{ value: 'p0_issues', label: 'P0' }, { value: 'p1_issues', label: 'P1' }]

export default function RanchIssuesList({ issuesData, onClick }) {
  const [issueType, setIssueType] = useState<any>(defaultIssueType);
  const history = useHistory();
  const [selectedPriority, setSelectedPriority] = useState<any>('p0_issues');
  const [isP0Issue, setIsP0Issue] = useState<boolean>(false);

  const maxWidth = useGetMaxWidth();
  const { t } = useTranslation();
  const { renderSelect } = useRenderReusableControls(t);

  // Issue type dropdown options
  const p0IssueTypeOptions = useMemo(() => {
    return [{
      value: 'default',
      label: 'Select Issue',
    }, ...P0_ISSUE_TYPES?.map((item) => ({
      value: item,
      label: toTitleCase(item),
    }))];
  }, [isP0Issue]);

  const p1IssueTypeOptions = useMemo(() => {
    return [{
      value: 'default',
      label: 'Select Issue',
    }, ...P1_ISSUE_TYPES?.map((item) => ({
      value: item,
      label: toTitleCase(item),
    }))];
  }, [isP0Issue]);

  // Filtered data (derived state instead of storing separately)
  const selectedData = useMemo(() => {
    if (!issuesData) return [];

    return issueType !== defaultIssueType
      ? issuesData?.[selectedPriority]
      ?.filter((item) => item?.type === issueType) : issuesData?.[selectedPriority];
  }, [issuesData, issueType, selectedPriority]);

  // Dynamic columns
  const columns = useMemo(() => {
    const allHeaders = {
      ...P0_ISSUE_TYPE_HEADERS,
      ...P1_ISSUE_TYPE_HEADERS,
    };

    const headers = allHeaders?.[issueType] || [];

    return headers.map((key: string) => ({
      title: t(toTitleCase(key)),
      dataIndex: key,
      key,
      width: 120,
      render: (_, record) => {
        if (key?.includes('identifier')) {
          return record?.[key] ? <Button type="text" title={t('Click to copy')} onClick={() => {
            navigator.clipboard.writeText(record?.[key]);
            notification.success({
              message: t('Copied to clipboard!'),
            });
          }}>{renderFieldValue(record?.[key])}</Button>
          : renderFieldValue(record?.[key]);
        }
        return renderFieldValue(record?.[key])
      },
    }));
  }, [issueType, t]);

  const onChangePriority = (val) => {
    if (val === 'p0_issues') setIsP0Issue(true);
    else if (val === 'p1_issues') setIsP0Issue(false);
    setIssueType(defaultIssueType)
    setSelectedPriority(val)
  }

  const titleStr = t('Issues');

  return (
    <div
      className="single-column-page padded desktop-container desktop-container-config"
      style={!isMobile ? { maxWidth } : {}}
    >
      <div className="flex-space-between align-baseline">
        <Button
          className="back-btn"
          shape="circle"
          size="large"
          icon={<FaChevronLeft size={20} />}
          onClick={onClick}
        />
        <div className="custom-page-header">
          <Typography.Title level={3}>
            {t(titleStr)}
          </Typography.Title>
        </div>
      </div>

      <div style={{ display: 'flex', marginBottom: 16 }}>
        <span style={{ width: 80, fontWeight: 'bold' }}>
          {t('Issue Types')}:
        </span>

        <div style={{ width: 220, marginTop: -5 }}>
          {isP0Issue && renderSelect(
            p0IssueTypeOptions,
            'Select Issue',
            issueType,
            setIssueType
          )}
          {!isP0Issue && renderSelect(
            p1IssueTypeOptions,
            'Select Issue',
            issueType,
            setIssueType
          )}
        </div>
        <span style={{ width: 80, fontWeight: 'bold' }}>
          {t('Priority')}:
        </span>

        <div style={{ width: 220, marginTop: -5 }}>
          {renderSelect(
            priorityOptions,
            'Select Priority',
            selectedPriority,
            onChangePriority,
          )}
        </div>
      </div>

      <EditableTable
        data={selectedData}
        columns={columns}
        showIsActive={false}
        showAction={false}
        rowClassName={(_, index) =>
          index % 2 === 0 ? '' : 'table-row-dark'
        }
      />
    </div>
  );
}
